import React, { useCallback, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { CheckboxWrapper, Headline3 } from 'venn-ui-kit';
import { Tooltip, TooltipTrigger, TooltipPortal, TooltipContentWithArrow } from '@/components/ui/tooltip';
import { StyledLabel, DelimitedWrapper, InfoIcon, getHideToggleConfig, GapWrapper } from '../shared';
import { StudioPrintSettingsContext, useDebounceToGlobal, UserContext } from 'venn-components';
import {
  viewPrimaryColor,
  studioSectionPageHasFullPageBackgroundImage,
  viewSectionTextColor,
  studioSectionPageHideCompanyName,
} from 'venn-state';
import { useRecoilState } from 'recoil';
import { ColorPicker } from './ColorPicker';
import { BackgroundImageGrid, PageWithBackgroundImage } from './BackgroundImageGrid';
import { useHasFF } from 'venn-utils';
import { ChromePicker } from 'react-color';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const SectionPageSettingsSection = () => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const { hasPermission, profileSettings } = useContext(UserContext);
  const theme = useContext(ThemeContext);

  const canCreateTemplate = hasPermission('STUDIO_CREATE_TEMPLATE');
  const [primaryColor, setPrimaryColor] = useRecoilState(viewPrimaryColor);
  const [textColor, setTextColor] = useRecoilState(viewSectionTextColor);
  const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);
  const [debouncedPrimaryColor, setDebouncedPrimaryColor] = useDebounceToGlobal(primaryColor, setPrimaryColor);
  const [debouncedTextColor, setDebouncedTextColor] = useDebounceToGlobal(textColor, setTextColor);
  const [hasFullPageBackgroundImage, setStudioSectionPageHasFullPageBackgroundImage] = useRecoilState(
    studioSectionPageHasFullPageBackgroundImage,
  );
  const hideSectionPageCompanyNameState = useRecoilState(studioSectionPageHideCompanyName);

  const Wrapper = hasCustomizationEnhancements ? DelimitedWrapper : GapWrapper;
  const sectionHeader = hasCustomizationEnhancements ? (
    <Headline3 className="mb-[8px]">Section Page Settings</Headline3>
  ) : (
    <StyledLabel htmlFor="cover-page-image">Section Page Color</StyledLabel>
  );

  const onFullPageBackgroundSettingChange = useCallback(
    (value: string) => setStudioSectionPageHasFullPageBackgroundImage(value === 'section-full-page-background'),
    [setStudioSectionPageHasFullPageBackgroundImage],
  );

  const rcmCustomSectionSettings = (
    <div className="gap-[30px] flex [&>*]:flex-1">
      <div className="flex flex-col flex-[0_0_50%]">
        <div className="flex flex-col gap-[6px]">
          <StyledLabel htmlFor="section-page-background">Section Page Background</StyledLabel>
          <RadioGroup onValueChange={onFullPageBackgroundSettingChange} className="flex-col gap-[12px]">
            <div className="flex gap-[4px]">
              <RadioGroupItem
                value="section-solid-color"
                id="section-solid-color"
                state={hasFullPageBackgroundImage ? 'unchecked' : 'checked'}
              />
              <Label htmlFor="section-solid-color" className="text-[12px] leading-[14px]">
                <ColorPicker color={debouncedPrimaryColor} onChange={setDebouncedPrimaryColor}>
                  Solid Color
                </ColorPicker>
              </Label>
            </div>
            <div className="flex gap-[4px]">
              <RadioGroupItem
                value="section-full-page-background"
                id="section-full-page-background"
                state={hasFullPageBackgroundImage ? 'checked' : 'unchecked'}
              />
              <Label htmlFor="section-full-page-background" className="text-[12px] leading-[14px]">
                Full Page Background Image
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon
                      type="info-circle"
                      prefix="far"
                      className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
                    />
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContentWithArrow>
                      When uploading a full page background image, your image must be: 1122 &times; 791px for Landscape
                      or 791 &times; 1122px for Portrait (JPG, PNG).
                    </TooltipContentWithArrow>
                  </TooltipPortal>
                </Tooltip>
              </Label>
            </div>
          </RadioGroup>
        </div>

        <BackgroundImageGrid
          page={PageWithBackgroundImage.SECTION_PAGE}
          singleColumnView
          canCreateTemplate={canCreateTemplate}
        />
      </div>

      <div className="flex flex-col flex-[0_0_50%] gap-[12px]">
        <div className="flex flex-col gap-[6px]">
          <StyledLabel htmlFor="section-page-text">Section Page Text</StyledLabel>
          <div className="flex-col gap-[12px]">
            <ColorPicker color={debouncedTextColor} onChange={setDebouncedTextColor}>
              Text Color
            </ColorPicker>
          </div>
        </div>

        <div className="flex flex-col gap-[6px]">
          <CheckboxWrapper
            {...getHideToggleConfig(hideSectionPageCompanyNameState)}
            disabled={!canCreateTemplate}
            alignItems="flex-start"
            spanStyle={{ paddingLeft: '4px' }}
            inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
          >
            <strong>Company Name{hasCustomizationEnhancements ? '' : ':'} </strong>{' '}
            <span className={hasCustomizationEnhancements ? '!text-venn-grey-80' : ''}>
              {organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name}
            </span>
          </CheckboxWrapper>
        </div>
      </div>
    </div>
  );
  const defaultSectionSettings = (
    <ChromePicker color={debouncedPrimaryColor} onChange={(color) => setDebouncedPrimaryColor(color.hex)} />
  );

  const sectionSettings = hasCustomizationEnhancements ? rcmCustomSectionSettings : defaultSectionSettings;

  return (
    <Wrapper>
      {sectionHeader}
      {canCreateTemplate ? sectionSettings : <ColorInput type="color" value={primaryColor} disabled />}
    </Wrapper>
  );
};

export default SectionPageSettingsSection;

const ColorInput = styled.input`
  padding: 0;
  border: 0;
`;
